// Simple list-table pages for the admin panel. Each component is intentionally compact:
// most heavy logic lives in admin_routes.py; the UI is a thin viewer + occasional refresh.

import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

function PaginatedTable({ title, endpoint, columns, rowKey = "id", actions, testid }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get(endpoint, { params: { limit: 25, skip } });
    setRows(data.items);
    setTotal(data.total);
  }, [endpoint, skip]);

  useEffect(() => { load(); }, [load]);

  if (!rows) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  return (
    <div className="space-y-5 fade-in" data-testid={testid}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">{title}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">{total} total · {endpoint}</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => <TableHead key={c.key} className={c.right ? "text-right" : ""}>{c.label}</TableHead>)}
              {actions && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r[rowKey]}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={c.right ? "text-right tabular-nums" : ""}>
                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                  </TableCell>
                ))}
                {actions && <TableCell className="text-right">{actions(r, load)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{skip + 1}–{Math.min(skip + 25, total)} of {total}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={skip <= 0} onClick={() => setSkip(Math.max(0, skip - 25))}>Prev</Button>
          <Button variant="outline" size="sm" disabled={skip + 25 >= total} onClick={() => setSkip(skip + 25)}>Next</Button>
        </div>
      </div>
    </div>
  );
}

export function AdminProperties() {
  return (
    <PaginatedTable
      title="Properties" endpoint="/admin/properties" testid="admin-properties"
      columns={[
        { key: "address", label: "Address" },
        { key: "city", label: "City" },
        { key: "country", label: "Country" },
        { key: "asking_price", label: "Asking", right: true, render: (r) => `$${(r.asking_price || 0).toLocaleString()}` },
        { key: "user_id", label: "User", render: (r) => <span className="font-mono text-xs">{(r.user_id || "").slice(0, 8)}…</span> },
      ]}
    />
  );
}

export function AdminCopilot() {
  return (
    <PaginatedTable
      title="Copilot conversations" endpoint="/admin/copilot/conversations" testid="admin-copilot"
      columns={[
        { key: "title", label: "Title", render: (r) => r.title || "—" },
        { key: "last_message_preview", label: "Preview", render: (r) => (r.last_message_preview || "").slice(0, 60) },
        { key: "updated_at", label: "Updated", render: (r) => (r.updated_at || "").slice(0, 16) },
        { key: "user_id", label: "User", render: (r) => <span className="font-mono text-xs">{(r.user_id || "").slice(0, 8)}…</span> },
      ]}
      actions={(r, reload) => (
        <Button size="sm" variant="ghost" data-testid={`del-conv-${r.id}`} onClick={async () => {
          if (!confirm("Delete this conversation?")) return;
          await api.delete(`/admin/copilot/conversations/${r.id}`);
          toast.success("Deleted");
          reload();
        }}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
      )}
    />
  );
}

export function AdminAiCalls() {
  return (
    <PaginatedTable
      title="AI calls log" endpoint="/admin/ai-calls" testid="admin-ai-calls"
      columns={[
        { key: "created_at", label: "When", render: (r) => (r.created_at || "").slice(0, 16) },
        { key: "endpoint", label: "Endpoint" },
        { key: "model", label: "Model" },
        { key: "status", label: "Status", render: (r) => <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${r.status === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{r.status}</span> },
        { key: "input_tokens", label: "In", right: true },
        { key: "output_tokens", label: "Out", right: true },
        { key: "latency_ms", label: "Latency (ms)", right: true },
      ]}
    />
  );
}

export function AdminFxRates() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = async () => { const { data } = await api.get("/admin/fx-rates"); setData(data); };
  useEffect(() => { load(); }, []);
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/fx-rates/refresh");
      setData(data);
      toast.success("FX cache refreshed");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const rates = data.rates || {};
  return (
    <div className="space-y-5 fade-in" data-testid="admin-fx-rates">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">FX Rates</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">USD-base exchange rates</h1>
          <p className="mt-1 text-sm text-slate-500">last_updated {data.last_updated?.slice(0, 19)} · source {data.source}</p>
        </div>
        <Button onClick={onRefresh} disabled={refreshing} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="fx-refresh">
          {refreshing ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <RefreshCw className="me-1 h-4 w-4" />} Refresh now
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {Object.entries(rates).slice(0, 60).map(([cc, rate]) => (
          <div key={cc} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{cc}</div>
            <div className="mt-1 text-lg font-bold tabular-nums text-slate-900">{Number(rate).toFixed(4)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminTaxRules() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/tax-rules").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  return (
    <div className="space-y-5 fade-in" data-testid="admin-tax-rules">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Tax Rules</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">Country tax reference table</h1>
        <p className="mt-1 text-sm text-slate-500">last reviewed {data.last_reviewed} · {data.note}</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {Object.entries(data.rules).map(([code, r]) => (
          <div key={code} data-testid={`tax-card-${code}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-baseline justify-between">
              <h3 className="text-base font-semibold text-slate-900">{r.country_name} <span className="text-slate-400 text-xs">({code})</span></h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">{r.currency}</span>
            </div>
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Property tax (annual)</dt><dd className="font-medium tabular-nums">{(r.default_property_tax_rate_annual * 100).toFixed(2)}%</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Transfer tax (one-time)</dt><dd className="font-medium tabular-nums">{(r.default_transfer_tax_rate * 100).toFixed(2)}%</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Closing costs (typical)</dt><dd className="font-medium tabular-nums">{(r.typical_closing_costs_pct * 100).toFixed(2)}%</dd></div>
            </dl>
            {Object.keys(r.state_overrides || {}).length > 0 && (
              <p className="mt-2 text-xs text-slate-500">Sub-region overrides: {Object.keys(r.state_overrides).join(", ")}</p>
            )}
            <p className="mt-3 text-xs leading-relaxed text-slate-500">{r.notes}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminAuditLog() {
  return (
    <PaginatedTable
      title="Audit log" endpoint="/admin/audit-log" testid="admin-audit-log"
      columns={[
        { key: "created_at", label: "When", render: (r) => (r.created_at || "").slice(0, 19) },
        { key: "admin_email", label: "Admin" },
        { key: "action", label: "Action" },
        { key: "target_collection", label: "Collection" },
        { key: "target_id", label: "Target", render: (r) => <span className="font-mono text-xs">{(r.target_id || "").slice(0, 12) || "—"}</span> },
        { key: "diff", label: "Diff", render: (r) => (
          <details className="text-xs">
            <summary className="cursor-pointer text-indigo-600">view</summary>
            <pre className="mt-1 max-w-[28rem] overflow-auto rounded bg-slate-50 p-2 text-[10px] text-slate-700">{JSON.stringify({ before: r.before, after: r.after }, null, 2)}</pre>
          </details>
        ) },
      ]}
    />
  );
}



// Phase 8a — Property-management admin views (read-only)

export function AdminTenants() {
  return (
    <PaginatedTable
      title="Tenants" endpoint="/admin/tenants" testid="admin-tenants"
      columns={[
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "status", label: "Status", render: (r) => (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{r.status}</span>
        ) },
        { key: "employment_type", label: "Employment" },
        { key: "created_at", label: "Created", render: (r) => (r.created_at || "").slice(0, 10) },
      ]}
    />
  );
}

export function AdminLeases() {
  return (
    <PaginatedTable
      title="Leases" endpoint="/admin/leases" testid="admin-leases"
      columns={[
        { key: "start_date", label: "Start" },
        { key: "end_date", label: "End" },
        { key: "monthly_rent", label: "Rent", right: true, render: (r) => `${r.monthly_rent} ${r.currency}` },
        { key: "term_months", label: "Term (mo)", right: true },
        { key: "status", label: "Status", render: (r) => (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{r.status}</span>
        ) },
      ]}
    />
  );
}

export function AdminMaintenance() {
  return (
    <PaginatedTable
      title="Maintenance" endpoint="/admin/maintenance" testid="admin-maintenance"
      columns={[
        { key: "title", label: "Title" },
        { key: "category", label: "Category" },
        { key: "priority", label: "Priority", render: (r) => {
          const tone = { emergency: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-700" }[r.priority] || "bg-slate-100 text-slate-700";
          return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}>{r.priority}</span>;
        } },
        { key: "status", label: "Status" },
        { key: "estimated_cost", label: "Est.", right: true, render: (r) => r.estimated_cost ? `$${r.estimated_cost}` : "—" },
        { key: "actual_cost", label: "Actual", right: true, render: (r) => r.actual_cost ? `$${r.actual_cost}` : "—" },
        { key: "reported_at", label: "Reported", render: (r) => (r.reported_at || "").slice(0, 10) },
      ]}
    />
  );
}
